import { Card, Button, Container, Row, Col, CardBody, FormGroup, Form, Input, Label, Modal } from 'reactstrap';
import { useContext, useState, useRef, useEffect } from 'react';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import { toast } from 'react-toastify';
import DocumentsFolders from '../../../services/DocumentsFolders';
import { DocumentsStateManager } from './DocumentsStateManager';

type FoldersIdProps = {
  folderId?: string;
  documentsState: DocumentsStateManager;
};

const saveDocument = async (
  e: React.FormEvent,
  folderName: string,
  hashFolderId: string,
  schoolId: string,
  typeFile: string,
  groupPermissions: string[],
  position: number,
  isPublic: boolean,
  folderId: string,
  token: string,
  picture: any,
  documentsState: DocumentsStateManager | undefined,
) => {
  e.preventDefault();

  let folderIdCreate = '';
  if (hashFolderId) {
    folderIdCreate = hashFolderId;
  }

  const body = {
    schoolId,
    documentsFoldersId: folderIdCreate,
    typeFile,
    name: folderName,
    groupPermissions,
    position,
    isPublic,
  };

  try {
    const result = folderId
      ? await DocumentsFolders(token).update(folderId, body)
      : await DocumentsFolders(token).create(body);

    if (picture) {
      const formData = new FormData();
      formData.append('picture', picture as any);
      await DocumentsFolders(token).picture(result?.data?.id, formData);
    }

    toast.success(`${folderId ? 'Updated' : 'Created'} successfully!`);
    if (result?.data?.id) {
      documentsState?.modalFolderShow();
      documentsState?.doRefresh();
    }
  } catch (e: any) {
    const message = String(e?.response?.data?.message || 'Create error!');
    toast.error(message);
  }
};

const DocumentsFoldersForm = ({ documentsState }: FoldersIdProps) => {
  const { token, currentSchool }: any = useContext(AuthContext);

  const folderId = documentsState?.state?.folderModal?.id;
  const hash = documentsState.state.hash || '';
  const hashFolderId = hash.replace('#', '');
  const isRootFolder = !documentsState.state.currentFolder?.documentsFoldersId;

  const schoolId = currentSchool?.value;
  const typeFile = documentsState?.state?.title ?? '';
  const position = 100;

  const [groupPermissions, setGroupPermissions] = useState<string[]>(
    documentsState.state.folderModal?.groupPermissions || [],
  );
  const imageRef = useRef(null as any);
  const [pictureUrl, setPictureUrl] = useState('');
  const [picture, setPicture] = useState();
  const [folderName, setFolderName] = useState(documentsState.state.folderModal?.name || '');
  const [isPublic, setIsPublic] = useState(Boolean);

  const [permissionsGroupsList, setPermissionsGroupsList] = useState<string[]>([]);

  const showOpenFileDialog = () => {
    if (imageRef?.current?.click) {
      imageRef.current.click();
    }
  };

  const onChangeFile = (event: any) => {
    event.stopPropagation();
    event.preventDefault();
    const file = event.target.files[0];
    setPicture(file);
    setPictureUrl(URL.createObjectURL(file));
  };

  const listPermissionsGroups = async () => {
    try {
      const result = documentsState.getPermissionsGroups();
      setPermissionsGroupsList(result || []);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'No permission groups found!');
    }
  };

  const getGroupPermissionsChecked = (value: string) => {
    const isChecked = groupPermissions.find((v: string) => v === value);
    return !!isChecked;
  };

  const setGroupPermissionsChecked = (value: string) => {
    const newGroupPermissionsSelected = JSON.parse(JSON.stringify(groupPermissions));
    if (newGroupPermissionsSelected.includes(value)) {
      const removedArray = newGroupPermissionsSelected.filter((v: string) => String(v) !== value);
      setGroupPermissions(removedArray);
      return;
    }
    newGroupPermissionsSelected.push(value);
    setGroupPermissions(newGroupPermissionsSelected);
  };

  const inputGroupPermissions = () => {
    try {
      return (
        <>
          <div className="modal-body">
            <FormGroup row>
              <Col md="12">
                <label className="form-control-label" htmlFor="activityType">
                  Access permission
                </label>
              </Col>
              {permissionsGroupsList.map((s: any) => (
                <Col md="12" key={`settings-input-${s.id}`}>
                  <FormGroup check>
                    <Label check>
                      <Input
                        name="activityType"
                        type="checkbox"
                        disabled={!isRootFolder}
                        defaultChecked={getGroupPermissionsChecked(s)}
                        onChange={() => setGroupPermissionsChecked(s)}
                      />{' '}
                      {s}
                    </Label>
                  </FormGroup>
                </Col>
              ))}
            </FormGroup>
          </div>
        </>
      );
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'No permission groups found!');
    }
  };

  useEffect(() => {
    listPermissionsGroups();
  }, []);

  useEffect(() => {
    listPermissionsGroups();
  }, [groupPermissions]);

  return (
    <Modal
      className={`modal-dialog-centered ${isRootFolder ? 'modal-xl' : 'modal-xs'}`}
      isOpen={documentsState.state.showFoldersForm}
      toggle={() => documentsState.modalFolderShow()}
    >
      <div className="modal-header">
        <h5 className="modal-title" id="Label">
          New Folder
        </h5>
        <button
          aria-label="Close"
          className="close"
          data-dismiss="modal"
          type="button"
          onClick={() => documentsState.modalFolderShow()}
        >
          <span aria-hidden={true}>×</span>
        </button>
      </div>
      <Container fluid>
        <Form
          onSubmit={(e) =>
            saveDocument(
              e,
              folderName,
              hashFolderId,
              schoolId,
              typeFile,
              groupPermissions,
              position,
              isPublic,
              folderId || '',
              token,
              picture,
              documentsState,
            )
          }
        >
          <Card>
            <CardBody>
              <Row>
                <Col>
                  <Row>
                    <Col lg="12">
                      <FormGroup>
                        <label className="form-control-label" htmlFor="input-name">
                          Folder Name
                        </label>
                        <Input
                          className="form-control-alternative"
                          id="input-name"
                          placeholder="Folder Name"
                          type="text"
                          value={folderName}
                          onChange={(e) => setFolderName(e.target.value)}
                          required
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col lg="12">
                      <Row style={{ display: 'grid' }}>
                        <Row>
                          <Col lg="12">
                            <h6 className="heading-small text-muted mb-4">
                              Picture <small style={{ textTransform: 'none' }}>(Recommended size: 800 x 800px)</small>
                            </h6>
                          </Col>
                        </Row>
                        <Row>
                          <Col lg="12" style={{ textAlign: 'center' }}>
                            <FormGroup>
                              <input
                                id="img-picture"
                                name="picture"
                                type="file"
                                accept=".png,.jpg,.jpeg"
                                style={{ display: 'none' }}
                                ref={imageRef}
                                onChange={onChangeFile}
                              />
                              <img
                                alt={documentsState.state.title}
                                className=""
                                src={pictureUrl ? `${pictureUrl}` : require('../../../assets/img/default.png').default}
                                style={{ width: 135, height: 135, cursor: 'pointer' }}
                                onClick={showOpenFileDialog}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </Row>
                    </Col>
                  </Row>

                  <Row>
                    <Col lg="12">
                      <FormGroup row>
                        <Col md="12">
                          <label className="form-control-label" htmlFor="isPublic">
                            Can be accessed out of school network?
                          </label>
                        </Col>
                        <Col md="12"></Col>
                        <Col md="12">
                          <label className="custom-toggle">
                            <Input type="checkbox" onChange={(e) => setIsPublic(e.target.checked)} checked={isPublic} />
                            <span className="custom-toggle-slider rounded-circle" />
                          </label>
                        </Col>
                      </FormGroup>
                    </Col>
                  </Row>
                </Col>
                {isRootFolder && (
                  <Col>
                    <Row>
                      <Col lg="12">{inputGroupPermissions()}</Col>
                    </Row>
                  </Col>
                )}
              </Row>
              <Row className="mt-5">
                <Col md="6 text-center" xs="6">
                  <Button color="primary" type="submit" size="lg" style={{ minWidth: '100px' }}>
                    Save
                  </Button>
                </Col>
                <Col md="6 text-center" xs="6">
                  <Button
                    color="secondary"
                    size="lg"
                    style={{ minWidth: '100px' }}
                    onClick={() => documentsState.modalFolderShow()}
                  >
                    Cancel
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Form>
      </Container>
    </Modal>
  );
};

export default DocumentsFoldersForm;
